import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { accessApi, type UsersQuery } from '@/api/accessApi'

/**
 * Users, roles and teams. Every write refreshes the access lists, the admin
 * audit and the assignee picker (`agents`), so a new or disabled agent shows
 * up (or drops out) everywhere.
 */
function useRefresh() {
  const queryClient = useQueryClient()
  return () => {
    for (const key of [['access'], ['admin', 'audit'], ['agents']]) void queryClient.invalidateQueries({ queryKey: key })
  }
}

export function useAdminUsers(q: UsersQuery, enabled = true) {
  return useQuery({ queryKey: ['access', 'users', q], queryFn: () => accessApi.users(q), enabled, retry: false })
}

export function useRoles(enabled = true) {
  return useQuery({ queryKey: ['access', 'roles'], queryFn: accessApi.roles, enabled, retry: false })
}

export function usePermissionCatalog(enabled = true) {
  return useQuery({ queryKey: ['access', 'permissions'], queryFn: accessApi.permissions, enabled, retry: false, staleTime: Infinity })
}

export function useTeams(enabled = true) {
  return useQuery({ queryKey: ['access', 'teams'], queryFn: accessApi.teams, enabled, retry: false })
}

/** Wraps any access API call as a mutation that refreshes everything access-related. */
export function useAccessMutation<A, R>(fn: (args: A) => Promise<R>) {
  const refresh = useRefresh()
  return useMutation({ mutationFn: fn, onSuccess: refresh })
}
